import React from 'react';
import { formatDate, weekdayName } from '../utils/dateConverter';

/** The admin's open overtime requests, as a dashboard to-do list. Props only, no services, so the
 *  layout and the waiting arithmetic are testable without the stack; `pages/KPI.js` fetches. */

/** Minutes as `HH:MM`, the same shape the approval tables use. */
export const fmtMinutes = (minutes) => {
    const abs = Math.abs(minutes ?? 0);
    return `${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

/** Whole days between a request's date and *today*, read field by field out of the bare
 *  `YYYY-MM-DD`: `new Date(iso)` is midnight UTC and comes out a day short west of UTC. */
export const daysWaiting = (isoDate, today = new Date()) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate ?? ''));
    if (!m) return 0;
    const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.max(0, Math.round((now - then) / 86400000));
};

/** How urgent the row looks. A fortnight is where "not yet" becomes "forgotten". */
export const waitingLevel = (days) => {
    if (days >= 14) return 'late';
    if (days >= 7) return 'due';
    return 'fresh';
};

export const waitingLabel = (days) => {
    if (days === 0) return 'heute erfasst';
    if (days === 1) return 'seit gestern offen';
    return `seit ${days} Tagen offen`;
};

export default function OvertimeTodoList({
    items = [],
    total = 0,
    loading = false,
    error = null,
    busyId = null,
    onApprove,
    onReject,
    onOpenAll,
    today,
}) {
    // The count comes from the server, not from `items.length`: the list is capped, and a
    // backlog that reads as "5 offen" while 40 are waiting is worse than no number.
    const count = total || items.length;
    const hidden = Math.max(0, count - items.length);

    return (
        <section className="kpi-todo-card" aria-labelledby="kpi-todo-heading">
            <div className="kpi-projects-header">
                <h3 id="kpi-todo-heading">Zu genehmigen</h3>
                <div className="kpi-todo-head-right">
                    {!loading && !error && count > 0 && (
                        <span className="kpi-count-badge kpi-count-badge--todo">
                            {count} {count === 1 ? 'Antrag' : 'Anträge'}
                        </span>
                    )}
                    {onOpenAll && (
                        <button type="button" className="kpi-todo-link" onClick={onOpenAll}>
                            Alle Anträge
                        </button>
                    )}
                </div>
            </div>

            {error ? (
                <p className="error-text">{error}</p>
            ) : loading ? (
                <p className="kpi-empty">Lade offene Anträge…</p>
            ) : items.length === 0 ? (
                // Said out loud rather than hiding the card: an admin has to be able to tell
                // "nothing to do" from "it did not load".
                <p className="kpi-empty">Keine offenen Überstunden-Anträge.</p>
            ) : (
                <ul className="kpi-todo-list">
                    {items.map((item) => {
                        const days = daysWaiting(item.date, today);
                        const busy = busyId === item.id;
                        return (
                            <li key={item.id} className="kpi-todo-row">
                                <div className="kpi-todo-main">
                                    <div className="kpi-todo-name" title={item.displayName || ''}>
                                        {item.displayName || 'Mitarbeiter'}
                                    </div>
                                    <div className="kpi-todo-meta">
                                        {weekdayName(item.date)}, {formatDate(item.date)}
                                        <span className={`kpi-todo-age is-${waitingLevel(days)}`}>
                                            {waitingLabel(days)}
                                        </span>
                                    </div>
                                </div>

                                <div className="kpi-todo-hours">
                                    <span className="kpi-todo-overtime">
                                        +{fmtMinutes(item.overtimeMinutes)}
                                    </span>
                                    <span className="kpi-todo-hours-raw">
                                        {fmtMinutes(item.actualMinutes)} statt{' '}
                                        {fmtMinutes(item.plannedMinutes)}
                                    </span>
                                </div>

                                <div className="kpi-todo-actions">
                                    <button
                                        type="button"
                                        className="button-success"
                                        disabled={busy}
                                        onClick={() => onApprove?.(item)}
                                    >
                                        Genehmigen
                                    </button>
                                    <button
                                        type="button"
                                        className="button-danger"
                                        disabled={busy}
                                        onClick={() => onReject?.(item)}
                                    >
                                        Ablehnen
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                    {hidden > 0 && (
                        // Never a silent cap: the list stops, the sentence says where.
                        <li className="kpi-todo-row kpi-todo-more">
                            +{hidden} weitere unter Mitarbeiter
                        </li>
                    )}
                </ul>
            )}
        </section>
    );
}
